import { gatewayApi as api } from './gatewayApi';

// Interfaces
export interface UOFM {
  Id?: number;
  Name: string;
  Description?: string;
  Active: boolean;
  CreatedDate?: string;
  ModifiedDate?: string;
}

export interface UOFMListResponse {
  data: UOFM[];
  total: number;
}

export interface UOFMSearchParams {
  page?: number;
  pageSize?: number;
  search?: string;
  active?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// API Service
export const uofmApi = {
  // CRUD Operations
  getAll: (params?: UOFMSearchParams): Promise<UOFMListResponse> =>
    api.get('/uom', params),

  getById: (id: number): Promise<UOFM> =>
    api.get(`/uom/${id}`),

  create: (uofm: Omit<UOFM, 'Id'>): Promise<UOFM> =>
    api.post('/uom', uofm),

  update: (id: number, uofm: Partial<UOFM>): Promise<UOFM> =>
    api.put(`/uom/${id}`, uofm),

  delete: (id: number): Promise<{ success: boolean; message: string }> =>
    api.delete(`/uom/${id}`),

  // Search and Filter
  search: (params: UOFMSearchParams): Promise<UOFMListResponse> =>
    api.post('/uom/search', params),

  // Bulk Operations
  bulkActivate: (ids: number[]): Promise<{ success: boolean; message: string }> =>
    api.put('/uom/bulk-activate', { ids }),

  bulkDeactivate: (ids: number[]): Promise<{ success: boolean; message: string }> =>
    api.put('/uom/bulk-deactivate', { ids }),

  bulkDelete: (ids: number[]): Promise<{ success: boolean; message: string }> =>
    api.post('/uom/bulk-delete', { ids }),

  // Statistics
  getStatistics: (): Promise<{
    total: number;
    active: number;
    inactive: number;
  }> =>
    api.get('/uom/statistics'),

  // Export
  exportToCSV: (params?: UOFMSearchParams): Promise<Blob> =>
    api.getBlob('/uom/export', { params }),

  // Validation
  validation: {
    validateUOFM: (uofm: Partial<UOFM>): string[] => {
      const errors: string[] = [];

      // Name validation
      if (!uofm.Name || uofm.Name.trim().length === 0) {
        errors.push('UOFM Name is required');
      } else if (uofm.Name.trim().length < 1) {
        errors.push('UOFM Name must be at least 1 character long');
      } else if (uofm.Name.trim().length > 50) {
        errors.push('UOFM Name must be less than 50 characters');
      }

      // Description validation (optional but if provided, validate length)
      if (uofm.Description && uofm.Description.trim().length > 200) {
        errors.push('Description must be less than 200 characters');
      }

      // Active validation
      if (uofm.Active === undefined || uofm.Active === null) {
        errors.push('Active status is required');
      }

      return errors;
    },

    validateBulkOperation: (ids: number[]): string[] => {
      const errors: string[] = [];

      if (!ids || ids.length === 0) {
        errors.push('At least one UOFM must be selected');
      }

      if (ids.length > 100) {
        errors.push('Cannot perform bulk operation on more than 100 items at once');
      }

      return errors;
    }
  }
};

export default uofmApi; 